/**
 * Inline SVG engine logos for the dashboard. Same paths as BrandAxisPage's
 * EngineLogo (kept local to avoid cross-page coupling on a private component).
 */
import { AiOverviewLogo, MistralLogo } from '../../components/brand/EngineLogo';

export type DashboardEngineKey =
  | 'perplexity' | 'openai' | 'chatgpt' | 'gemini' | 'claude' | 'mistral' | 'ai_overview' | string;

export const ENGINE_LABEL: Record<string, string> = {
  perplexity: 'Perplexity',
  openai: 'ChatGPT',
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  mistral: 'Mistral',
  ai_overview: 'AI Overview',
  google_ai_mode: 'Google AI Mode',
};

export const ENGINE_COLOR: Record<string, string> = {
  perplexity: '#20B8CD',
  openai: '#10A37F',
  chatgpt: '#10A37F',
  gemini: '#4285F4',
  claude: '#D97757',
  mistral: '#FA500F',
  ai_overview: '#EA4335',
  google_ai_mode: '#9334E6',
};

export function EngineLogo({ engine, size = 20 }: { engine: string; size?: number }) {
  const k = (engine || '').toLowerCase();
  if (k === 'perplexity') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#20B8CD" aria-label="Perplexity">
        <path d="M12 2.5 3.5 7.5v9L12 21.5l8.5-5v-9L12 2.5Zm0 2.31 6.5 3.82v7.74L12 19.19l-6.5-3.82V8.63L12 4.81Z" />
        <path d="M7.5 9.5h9v1.5h-9zM7.5 12h9v1.5h-9z" />
      </svg>
    );
  }
  if (k === 'openai' || k === 'chatgpt') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#10A37F" aria-label="ChatGPT">
        <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.52-2.9A6 6 0 0 0 5.07 4.16a5.98 5.98 0 0 0-4 2.91 6.05 6.05 0 0 0 .75 7.11 5.98 5.98 0 0 0 .52 4.91 6.05 6.05 0 0 0 6.52 2.9A6 6 0 0 0 18.93 19.84a5.98 5.98 0 0 0 4-2.91 6.05 6.05 0 0 0-.65-7.11ZM13.19 21.1a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.77-2.76a.78.78 0 0 0 .39-.68v-6.75L17.63 11v5.57a4.5 4.5 0 0 1-4.44 4.53Zm-9.66-4.12a4.48 4.48 0 0 1-.54-3.03l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33L10.7 18.5a4.5 4.5 0 0 1-6.17-1.52ZM2.56 8.66a4.49 4.49 0 0 1 2.34-1.97v5.68a.77.77 0 0 0 .39.67l5.81 3.35-2.02 1.16a.07.07 0 0 1-.07 0l-4.83-2.79a4.5 4.5 0 0 1-1.62-6.1Zm16.57 3.86-5.83-3.4 2.02-1.16a.07.07 0 0 1 .07 0l4.82 2.79a4.5 4.5 0 0 1-.67 8.1v-5.68a.78.78 0 0 0-.41-.65Zm2.02-3.03-.14-.09L16.23 6.6a.77.77 0 0 0-.79 0L9.62 9.97V7.64l4.83-2.79a4.5 4.5 0 0 1 6.7 4.64ZM8.52 13.57 6.5 12.4V6.84a4.5 4.5 0 0 1 7.38-3.46l-.14.08L8.97 6.22a.78.78 0 0 0-.39.67Zm1.1-2.37L12.22 9.67l2.62 1.5v3.01l-2.6 1.51-2.62-1.5Z" />
      </svg>
    );
  }
  if (k === 'gemini') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Gemini">
        <defs>
          <linearGradient id={`gm-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="50%" stopColor="#9B72F2" />
            <stop offset="100%" stopColor="#D96570" />
          </linearGradient>
        </defs>
        <path fill={`url(#gm-${size})`} d="M12 2c.4 4.6 2.4 6.6 7 7-4.6.4-6.6 2.4-7 7-.4-4.6-2.4-6.6-7-7 4.6-.4 6.6-2.4 7-7Z" />
      </svg>
    );
  }
  if (k === 'claude') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="#D97757" aria-label="Claude">
        <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z" />
      </svg>
    );
  }
  if (k === 'mistral') {
    return <MistralLogo size={size} />;
  }
  if (k === 'ai_overview' || k === 'google_ai_mode') {
    return <AiOverviewLogo size={size} />;
  }
  // Unknown engine — neutral dot.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#8A8A9C" aria-label={engine}>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}
